import { type Cohort, getConcentrationPlaylistPackIds, getRequiredStreamPackIdsFromPeriods } from 'Cohorts';
import { type ProjectsForPlaylists } from 'LearnerProjects';
import { type Playlist } from 'Playlists';
import { type useContentProgressResponse } from './ContentProgress.types';
import { isPassedOrWaived } from './ProjectProgress';

/**
 * Calculate the percent complete for the curriculum (streams and projects) in a cohort.
 *
 * It includes all courses that are either in concentration playlists or required in the schedule. This means that
 * we include non-required foundations streams in here. We do that because they show up in the UI as part of the
 * curriculum, so it would be odd if the curriculum was not all filled in, but at the top it said 100% complete. Also,
 * we want it to be clear to someone who hasn't finished all of foundations that there is still more for them to do,
 * even if it isn't required. See https://trello.com/c/wWAhZqLY
 */
export function getCurriculumPercentComplete(
    cohort: Cohort,
    playlists: Playlist[],
    projectsForPlaylists: ProjectsForPlaylists,
    contentProgress: useContentProgressResponse,
): number {
    const concentrationPackIds = getConcentrationPlaylistPackIds(cohort);
    const concentrationPlaylists = playlists.filter(p => concentrationPackIds.includes(p.localePackId));

    if (concentrationPlaylists.length !== concentrationPackIds.length) {
        throw new Error('Loaded Playlists does not match Cohort#concentrationPlaylistPackIds');
    }

    // Get unique stream pack ids from concentration playlists and schedule
    const playlistStreamPackIds = concentrationPlaylists.flatMap(p => p.streamEntries.map(e => e.localePackId));
    const scheduleStreamPackIds = getRequiredStreamPackIdsFromPeriods(cohort);
    const streamPackIds = [...new Set([...playlistStreamPackIds, ...scheduleStreamPackIds])];

    // Get unique projects from concentration playlists
    const projects = [
        ...new Map(
            concentrationPlaylists
                .flatMap(p => projectsForPlaylists.projectsByPlaylist[p.localePackId] ?? [])
                .map(p => [p.requirementIdentifier, p]),
        ).values(),
    ];

    const numCompletedStreams = streamPackIds.filter(
        id => contentProgress.streamProgressByLocalePackId[id]?.completedAt != null,
    ).length;

    const numCompletedProjects = projects.filter(project => {
        const progress = contentProgress.projectProgressByRequirementIdentifier[project.requirementIdentifier];
        return progress && isPassedOrWaived(progress);
    }).length;

    const numItems = streamPackIds.length + projects.length;
    const numCompletedItems = numCompletedStreams + numCompletedProjects;
    return numItems === 0 ? 0 : Math.round((numCompletedItems / numItems) * 100);
}
